import React from "react";

import { Building2, Calendar, ArrowRight, AlertCircle } from "lucide-react";

import Button from "../../Components/Button";
import { DisconnectResolution } from "../../Store/Reducers/AssessmentSlice";
import {
  getDisconnectActionLabel,
  getDisconnectActionPillBadgeClass,
} from "../../Utils/Constants/disconnectStatusLabels";
import {
  formatAssessmentDateTimePartsUtc,
  formatAssessmentDateUtc,
  isUtcDateOverdue,
  parseAssessmentDate,
  serverNow,
} from "../../Utils/Helper/assessmentDateUtils";

type DisconnectResolutionCardProps = {
  resolution: DisconnectResolution;
  canRecordResolution: boolean;
  canEditResolution: boolean;
  onViewDetails: (resolution: DisconnectResolution) => void;
  onRecordResolution: (resolution: DisconnectResolution) => void;
};

const MetaItem = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center gap-1">
    <span className="text-xs font-semibold text-gray-700">{label}</span>
    <span className="text-xs font-medium text-gray-600">{value}</span>
  </div>
);

const getDaysOverdue = (dueDate?: string | null): number => {
  if (!dueDate) return 0;
  const due = parseAssessmentDate(dueDate);
  if (!due || !isUtcDateOverdue(dueDate)) return 0;
  const today = serverNow();
  const todayMs = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );
  const dueMs = Date.UTC(
    due.getUTCFullYear(),
    due.getUTCMonth(),
    due.getUTCDate()
  );
  return Math.floor((todayMs - dueMs) / 86400000);
};

const tierBadge = (tier: string) => {
  const isStrong = tier === "strong";
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        isStrong
          ? "border-red-200 bg-red-50 text-red-700"
          : "border-amber-200 bg-amber-50 text-amber-700"
      }`}
    >
      {isStrong ? "Strong Disconnect" : "Soft Disconnect"}
    </span>
  );
};

const statusBadge = (status: string, hubPublished?: boolean) => (
  <span className={getDisconnectActionPillBadgeClass(status, hubPublished)}>
    {getDisconnectActionLabel(status, hubPublished)}
  </span>
);

const DisconnectResolutionCard: React.FC<DisconnectResolutionCardProps> = ({
  resolution,
  canRecordResolution,
  canEditResolution,
  onViewDetails,
  onRecordResolution,
}) => {
  const overdueDays = getDaysOverdue(resolution.due_date);
  const isOverdue =
    overdueDays > 0 && resolution.status === "pending_resolution";
  const dueDateLabel = resolution.due_date
    ? formatAssessmentDateTimePartsUtc(resolution.due_date, "DMY_SHORT").date
    : "—";
  const triggeredOnLabel = resolution.triggered_at
    ? formatAssessmentDateUtc(resolution.triggered_at, "US_SHORT")
    : "—";
  const showRecordResolution =
    resolution.status === "pending_resolution" && canRecordResolution;
  const showEditResolution =
    resolution.status === "resolved" && canEditResolution;
  const showStatusBadge = !showRecordResolution && !showEditResolution;
  const cardTitle =
    resolution.assessmentName?.trim() ||
    resolution.accountName ||
    "Unknown Assessment";

  return (
    <div className="group relative flex flex-col justify-between gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-all duration-200 hover:border-gray-300 hover:shadow-md">
      <div className="space-y-2.5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="m-0 min-w-0 flex-1 break-words text-base font-semibold leading-snug text-gray-900">
            {cardTitle}
          </h3>
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
            {tierBadge(resolution.delta_tier)}
            {resolution.combined ? (
              <span
                className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-700"
                title="One disconnect for the Formal and Team published together"
              >
                Combined
              </span>
            ) : null}
            {showStatusBadge &&
              statusBadge(resolution.status, resolution.hubPublished)}
          </div>
        </div>

        <div className="flex items-start justify-between gap-3">
          <p className="m-0 flex-1 text-sm leading-snug text-gray-600">
            {resolution.context_note ||
              "Disconnect workflow triggered for this account. Review sponsor inputs and resolve the final level."}
          </p>
          <div className="flex shrink-0 items-center gap-1.5">
            {isOverdue && (
              <span className="inline-flex items-center gap-1 rounded border border-orange-100 bg-orange-50 px-2 py-0.5 text-[11px] font-medium text-orange-500">
                <AlertCircle className="h-3 w-3" />
                Overdue
              </span>
            )}
            <div
              className={`flex items-center gap-1.5 rounded border px-2 py-1 ${
                isOverdue
                  ? "border-orange-100 bg-orange-50"
                  : "border-gray-200 bg-gray-50"
              }`}
            >
              <Calendar
                className={`h-3 w-3 ${
                  isOverdue ? "text-orange-400" : "text-gray-500"
                }`}
              />
              <span className="text-[11px] font-medium text-gray-600">
                Due:
              </span>
              <span
                className={`whitespace-nowrap text-[11px] font-semibold ${
                  isOverdue ? "text-orange-500" : "text-gray-900"
                }`}
              >
                {dueDateLabel}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="h-px bg-gray-200" />

      <div className="space-y-2.5">
        <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2">
          <Building2
            className="mr-2 h-3.5 w-3.5 shrink-0 text-gray-500"
            size={14}
            aria-label="Account"
          />
          <span className="truncate text-sm font-medium text-gray-900">
            {resolution.accountName || "Unknown Account"}
          </span>
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <MetaItem label="Triggered On:" value={triggeredOnLabel} />
            <MetaItem
              label="Triggered By:"
              value={resolution.triggeredByName || "System"}
            />
          </div>

          <div className="flex shrink-0 items-center gap-1.5">
            <Button
              variant="outline-primary"
              onClick={() => onViewDetails(resolution)}
              className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-xs font-semibold"
            >
              View Details
            </Button>
            {showRecordResolution && (
              <Button
                variant="primary"
                onClick={() => onRecordResolution(resolution)}
                className="inline-flex items-center justify-center gap-1 rounded-md border-0 bg-[#974ae4] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#853bd4]"
              >
                Record Resolution
                <ArrowRight className="h-3.5 w-3.5" size={14} />
              </Button>
            )}
            {showEditResolution && (
              <Button
                variant="primary"
                onClick={() => onRecordResolution(resolution)}
                className="inline-flex items-center justify-center gap-1 rounded-md border-0 bg-[#974ae4] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#853bd4]"
              >
                Edit Resolution
                <ArrowRight className="h-3.5 w-3.5" size={14} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DisconnectResolutionCard;
